import { useEffect, useState } from 'react';
import { Users, BookOpen, TrendingUp, Activity } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await api.get('/api/admin/analytics/overview');
      setStats(response.data.overview);
      setRecentActivity(response.data.recentActivity || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Всего пользователей',
      value: stats?.totalUsers || 0,
      icon: Users,
      color: 'bg-blue-500',
      trend: '+12% за месяц',
    },
    {
      title: 'Всего сессий',
      value: stats?.totalSessions || 0,
      icon: Activity,
      color: 'bg-green-500',
      trend: `${stats?.sessionsLastWeek || 0} за неделю`,
    },
    {
      title: 'Завершенных сессий',
      value: stats?.completedSessions || 0,
      icon: BookOpen,
      color: 'bg-purple-500',
      trend: `${Math.round((stats?.completedSessions / (stats?.totalSessions || 1)) * 100)}% завершено`,
    },
    {
      title: 'Средний балл',
      value: stats ? `${Math.round(stats.averageScore)}%` : '0%',
      icon: TrendingUp,
      color: 'bg-orange-500',
      trend: 'Общая оценка',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Обзор платформы</h1>
        <p className="text-gray-600 mt-1">Основная статистика и активность</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((card, index) => (
          <div
            key={index}
            className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`${card.color} p-3 rounded-lg`}>
                <card.icon className="w-6 h-6 text-white" />
              </div>
            </div>
            <h3 className="text-gray-600 text-sm font-medium mb-1">{card.title}</h3>
            <p className="text-3xl font-bold text-gray-900 mb-2">{card.value}</p>
            <p className="text-sm text-gray-500">{card.trend}</p>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Последняя активность</h2>
        </div>
        <div className="p-6">
          {recentActivity.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Нет недавней активности</p>
          ) : (
            <div className="space-y-4">
              {recentActivity.map((activity, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg"
                >
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {activity.action}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {activity.user_email} • {new Date(activity.created_at).toLocaleString('ru-RU')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-600 mb-2">Сессий за неделю</h3>
          <p className="text-2xl font-bold text-gray-900">
            {stats?.sessionsLastWeek || 0}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-600 mb-2">Сессий за месяц</h3>
          <p className="text-2xl font-bold text-gray-900">
            {stats?.sessionsLastMonth || 0}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-600 mb-2">Активных пользователей</h3>
          <p className="text-2xl font-bold text-gray-900">
            {stats?.totalUsers || 0}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
